import { activitiesService } from "@data/services/activitiesService"
import { useEffect, useRef, useState } from "react"

/**
 * Loads the prerequisite quiz correction for an activity.
 *
 * Two surfaces need it — the inline review in Sam and the retroactive modal —
 * and they get it two different ways: Sam already holds the payload from the
 * submit response, the modal has to fetch. This hook covers both so the
 * loading/error handling lives in one place.
 *
 * The correction is frozen once the attempt exists, so it is fetched once per
 * activity and kept: reopening the modal costs nothing.
 *
 * @param {Object} params
 * @param {number} [params.activityId] - Fetch the correction for this activity
 * @param {Object} [params.providedData] - Already-loaded QuizResource; skips the fetch
 * @param {boolean} [params.enabled=true] - Hold the fetch back (e.g. modal closed)
 * @returns {{ data: Object|null, loading: boolean, error: string|null }}
 */
export function useQuizCorrectionData({
    activityId,
    providedData,
    enabled = true,
}) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [fetchedData, setFetchedData] = useState(null)
    const fetchedForActivity = useRef(null)

    useEffect(() => {
        if (!enabled || providedData || !activityId) {
            return
        }

        // The "already fetched" check lives inside the effect, never in its
        // dependencies: deriving it from the ref would flip the dependency as
        // soon as the ref is set, React would run this effect's cleanup, and the
        // in-flight response would be discarded as cancelled — leaving the modal
        // spinning forever.
        if (fetchedForActivity.current === activityId) {
            return
        }

        let cancelled = false
        fetchedForActivity.current = activityId
        setLoading(true)
        setError(null)

        activitiesService
            .getPrerequisiteQuiz({ activityId })
            .then((response) => {
                if (cancelled) return
                setFetchedData(response?.data?.data || response?.data)
            })
            .catch((err) => {
                if (cancelled) return
                // Allow a retry on the next open rather than caching the failure
                fetchedForActivity.current = null
                setError(
                    err?.response?.data?.message ||
                        "Impossible de récupérer la correction du quiz."
                )
            })
            .finally(() => {
                if (cancelled) return
                setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [enabled, activityId, providedData])

    return {
        data: providedData || fetchedData,
        loading,
        error,
    }
}
